import { modelData } from 'app/lib/modelData'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CONTACT_MESSAGES_QUERY } from './graphql'
import { MARK_MESSAGE_READ_MUTATION } from 'app/graphql/messageMutations'
import { SEND_SMS_MUTATION } from 'app/components/contacts/inbox/graphql'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { excludeHeld } from 'app/lib/heldMessages'
import { ContactJson, MessageJson } from 'app/types/entities'
import type { Mutation } from 'app/generated/graphql'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/** A thread message plus the local-only state an optimistic send needs. */
export type ThreadMessage = MessageJson & { pending?: boolean; failed?: boolean }

export type UseContactMessagesReturn = {
  messages: ThreadMessage[]
  loading: boolean
  sending: boolean
  /** A load failed. Distinct from an empty thread, which is not an error. */
  failed: boolean
  /** Inbound messages the practice has not read yet. */
  unreadCount: number
  send: (text: string) => Promise<boolean>
  markAllRead: () => void
  /** Re-read the thread: on open, on a poll tick, and after a failure. */
  refresh: () => void
}

/**
 * A message is inbound when the contact sent it: nobody on staff sent it
 * (`sentByUserId`) and the AI agent did not either (`isAgent`). This is the
 * app's existing rule — `getMessageType` in the contact inbox's MessageCard and
 * `getUnreadMessageCount` in the main inbox both use it.
 *
 * The API also exposes a computed `direction`, but it is null on ~90% of rows
 * on dev, so it can only be a cross-check, never the source. Verified against
 * every row that does carry one: the two agree.
 */
function isInbound(message: MessageJson): boolean {
  return !message.sentByUserId && !message.isAgent
}

function isUnread(message: MessageJson): boolean {
  return isInbound(message) && !message.read
}

/**
 * Every way a contact's number might be stored on a message, so a thread is
 * found whether it was written bare, with a country code, or E.164.
 */
function phoneVariants(contact?: ContactJson): string[] {
  const found = new Set<string>()
  for (const raw of [contact?.cellPhone, contact?.homePhone, contact?.workPhone]) {
    const digits = String(raw ?? ``).replace(/\D/g, ``)
    if (digits.length < 10) continue
    const last10 = digits.slice(-10)
    found.add(last10)
    found.add(`1${last10}`)
    found.add(`+1${last10}`)
  }
  return [...found]
}

/**
 * A thread is the pair of phone numbers within one location, NOT the contact.
 *
 * `contactId` looks like the obvious key and is wrong: dev has 27 contacts
 * sharing one number, and messages within a single `threadId` carry different
 * contactIds depending on which duplicate was on screen when each was sent.
 * Keying on the contact returned 2 of that thread's messages and showed an
 * empty conversation on every other duplicate.
 *
 * Location is part of the key, not an optimization. One number's traffic can
 * span offices (a dev number splits 9/31 across two), and each office is its
 * own conversation on its own tracking number. Without this scope a contact at
 * location A would read a thread that happened at location B.
 */
function threadSearch(contact?: ContactJson): string | undefined {
  const variants = phoneVariants(contact)
  if (!variants.length || !contact?.locationId) return undefined
  const list = variants.map((value) => `"${value}"`).join(` OR `)
  return excludeHeld(`(from:(${list}) OR to:(${list})) AND channel:sms AND locationId:"${contact.locationId}"`)
}

/**
 * The contact's SMS thread for the focus card's conversation face.
 *
 * One-shot fetch per contact, deliberately NOT a urql subscription: a live
 * `useQuery` in this component tree re-rendered the focus card on every emit
 * and spun the main thread into a 250-renders-per-second loop. See
 * `useLocationsInfo` for the same constraint and the full story.
 */
export function useContactMessages(contact?: ContactJson): UseContactMessagesReturn {
  const [messages, setMessages] = useState<ThreadMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)
  // Guards against a slower fetch for a previous contact landing last and
  // painting their thread onto whoever is on screen now.
  const generation = useRef(0)
  const contactId = contact?.id
  const search = threadSearch(contact)

  const load = useCallback(
    ({ quiet }: { quiet?: boolean } = {}) => {
      generation.current += 1
      const token = generation.current
      if (!search) {
        setMessages([])
        setLoading(false)
        return
      }
      // A refresh keeps the thread on screen while it re-reads; only a cold
      // load shows the spinner.
      if (!quiet) setLoading(true)
      bg(
        gqlQuery<{ messages?: { hits?: MessageJson[] } }>(
          CONTACT_MESSAGES_QUERY,
          {
            search,
            pagination: { limit: 200 },
            sort: { field: `createdDate`, direction: `ASC` },
          },
          // Must bypass the document cache. urql defaults to cache-first, and an
          // inbound reply lands on the server without invalidating anything
          // here, so a cached read would return the same thread forever and the
          // poll below would be decorative.
          { requestPolicy: `network-only` },
        )
          .then((result) => {
            if (token !== generation.current) return
            if (result.error) {
              // Never let a failed read look like an empty conversation.
              logError(`Action Center failed to load thread`, result.error)
              setFailed(true)
              setLoading(false)
              return
            }
            const hits = result.data?.messages?.hits ?? []
            // Keep optimistic and failed bubbles the server does not know about
            // yet, so a refresh mid-send never drops what was just typed.
            setMessages((current) => {
              const local = current.filter((m) => (m.pending || m.failed) && !hits.some((hit) => hit.id === m.id))
              return [...hits, ...local]
            })
            setFailed(false)
            setLoading(false)
          })
          .catch((err) => {
            if (token !== generation.current) return
            logError(`Action Center failed to load thread`, err)
            setFailed(true)
            setLoading(false)
          }),
      )
    },
    [search],
  )

  useEffect(() => {
    setMessages([])
    setFailed(false)
    load()
  }, [contactId, load])

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const body = text.trim()
      // No contact id required: the send accepts a bare number.
      if (!body || !contact?.cellPhone || !contact.locationId) return false
      // Optimistic bubble so the thread feels immediate; a contact write on this
      // API runs ~2.5s, and a send is no faster.
      const tempId = `pending-${generation.current}-${messages.length}`
      const optimistic: ThreadMessage = {
        id: tempId,
        text: body,
        to: contact.cellPhone,
        from: ``,
        // Marks it outbound for MessageCard without claiming a real user id.
        sentByUserId: `pending`,
        createdDate: new Date().toISOString(),
        pending: true,
      } as ThreadMessage
      setMessages((current) => [...current, optimistic])
      setSending(true)
      try {
        const result = await gqlMutation(SEND_SMS_MUTATION, {
          input: {
            to: contact.cellPhone,
            text: body,
            locationId: contact.locationId,
            contactId: contact.id || undefined,
          },
        })
        const sent = modelData(result.data?.sendSms)
        if (result.error || !sent?.id) {
          setMessages((current) => current.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m)))
          return false
        }
        setMessages((current) => current.map((m) => (m.id === tempId ? sent : m)))
        return true
      } catch (err) {
        logError(`Action Center SMS send failed`, err)
        setMessages((current) => current.map((m) => (m.id === tempId ? { ...m, pending: false, failed: true } : m)))
        return false
      } finally {
        setSending(false)
      }
    },
    [contact, messages.length],
  )

  const markAllRead = useCallback(() => {
    const unread = messages.filter(isUnread)
    if (!unread.length) return
    setMessages((current) => current.map((m) => (isUnread(m) ? { ...m, read: true } : m)))
    for (const message of unread) {
      if (!message.id) continue
      bg(
        gqlMutation(MARK_MESSAGE_READ_MUTATION, {
          id: message.id,
          read: true,
        }).catch((err) => logError(`Action Center failed to mark message read`, err)),
      )
    }
  }, [messages])

  const unreadCount = useMemo(() => messages.filter(isUnread).length, [messages])

  // Stable identity: the poll interval keys off this, and a new function each
  // render would tear the timer down and rebuild it before it ever fired.
  const refresh = useCallback(() => load({ quiet: true }), [load])

  return { messages, loading, sending, failed, unreadCount, send, markAllRead, refresh }
}
